import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, ChevronLeft, Settings2, Lock } from 'lucide-react';
import { getConsent, saveConsent, applyConsent } from '../../lib/gtag';

const CATEGORIES = [
  { key: 'essential', label: 'Technisch notwendig', desc: 'Login, Sicherheit, Warenkorb & Zahlung — ohne diese funktioniert die Seite nicht.', locked: true },
  { key: 'analytics', label: 'Statistik', desc: 'Google Analytics 4 — anonymisierte Messung von Seitenaufrufen zur Verbesserung der Plattform.' },
  { key: 'marketing', label: 'Marketing', desc: 'Google Ads & Meta Pixel — messen, ob eine Anfrage oder ein Kauf auf eine Anzeige zurückgeht.' },
];

const Toggle = ({ on, onChange, testId, locked, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={locked || on}
    aria-label={`${label} ${locked ? '(immer aktiv)' : (on ? 'deaktivieren' : 'aktivieren')}`}
    onClick={() => !locked && onChange(!on)}
    data-testid={testId}
    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 after:content-[''] after:absolute after:-inset-2.5 ${
      locked ? 'bg-emerald-600/50 cursor-not-allowed' : (on ? 'bg-emerald-500' : 'bg-stone-700')
    }`}
  >
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${(locked || on) ? 'translate-x-4' : ''}`} />
  </button>
);

export default function CookieBanner() {
  const [visible, setVisible] = useState(() => !getConsent());
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const c = getConsent();
    if (c) applyConsent(c);
  }, []);

  // Bannerhöhe als CSS-Variable exportieren → Sticky-Buy-Bar & Co. weichen aus (kein Verdecken von CTAs).
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) { root.style.setProperty('--fx-cookie-h', '0px'); return undefined; }
    const set = () => root.style.setProperty('--fx-cookie-h', `${(boxRef.current?.offsetHeight || 0) + 24}px`);
    set();
    const t = setTimeout(set, 60);
    window.addEventListener('resize', set);
    return () => { clearTimeout(t); window.removeEventListener('resize', set); root.style.setProperty('--fx-cookie-h', '0px'); };
  }, [visible, showSettings]);

  const decide = (a, m) => {
    const choice = a && m ? 'all' : (!a && !m ? 'essential' : 'custom');
    saveConsent({ analytics: a, marketing: m, choice });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-[90] animate-fade-in" data-testid="cookie-banner">
      <div ref={boxRef} className="bg-stone-950 text-white rounded-2xl shadow-2xl border border-stone-800 p-5">
        {!showSettings ? (
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-4 h-4 text-emerald-400" strokeWidth={2} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium mb-1">Cookies &amp; Messung</p>
              <p className="text-xs text-stone-400 leading-relaxed">
                Notwendige Cookies nutzen wir immer. Statistik &amp; Marketing nur mit deiner Einwilligung.{' '}
                <Link to="/cookies" className="underline text-stone-300 hover:text-white">Details</Link>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => decide(true, true)}
                  className="px-4 py-2.5 rounded-full bg-white text-stone-900 text-xs font-medium hover:bg-stone-200 transition-colors"
                  data-testid="cookie-accept-all"
                >
                  Alle akzeptieren
                </button>
                <button
                  onClick={() => decide(false, false)}
                  className="px-4 py-2.5 rounded-full border border-stone-700 text-stone-300 text-xs font-medium hover:bg-stone-800 transition-colors"
                  data-testid="cookie-reject-all"
                >
                  Alle ablehnen
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-4 py-2.5 rounded-full text-stone-400 text-xs font-medium hover:text-white transition-colors inline-flex items-center gap-1.5"
                  data-testid="cookie-settings-btn"
                >
                  <Settings2 className="w-3.5 h-3.5" strokeWidth={2} /> Einstellungen
                </button>
              </div>
            </div>
            <button onClick={() => decide(false, false)} aria-label="Cookie-Hinweis schließen — nur notwendige Cookies erlauben" className="text-stone-400 hover:text-white w-11 h-11 -m-3 flex items-center justify-center flex-shrink-0" data-testid="cookie-banner-close">
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        ) : (
          <div data-testid="cookie-settings-panel">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setShowSettings(false)}
                className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-white transition-colors"
                data-testid="cookie-settings-back"
              >
                <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} /> Zurück
              </button>
              <p className="text-sm font-medium">Cookie-Einstellungen</p>
            </div>
            <div className="space-y-2.5">
              {CATEGORIES.map((c) => {
                const on = c.key === 'analytics' ? analytics : c.key === 'marketing' ? marketing : true;
                const onChange = c.key === 'analytics' ? setAnalytics : setMarketing;
                return (
                  <div key={c.key} className="rounded-xl border border-stone-800 bg-stone-900/60 px-3.5 py-3 flex items-start gap-3" data-testid={`cookie-category-${c.key}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium flex items-center gap-1.5">
                        {c.label}
                        {c.locked && <Lock className="w-3 h-3 text-stone-500" strokeWidth={2} />}
                      </p>
                      <p className="text-[11px] text-stone-400 leading-snug mt-1">{c.desc}</p>
                    </div>
                    <Toggle on={on} onChange={onChange} locked={c.locked} label={c.label} testId={`cookie-toggle-${c.key}`} />
                  </div>
                );
              })}
            </div>
            <div className="mt-3.5 flex flex-wrap gap-2">
              <button
                onClick={() => decide(analytics, marketing)}
                className="px-4 py-2.5 rounded-full bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-400 transition-colors"
                data-testid="cookie-save-selection"
              >
                Auswahl speichern
              </button>
              <button
                onClick={() => decide(true, true)}
                className="px-4 py-2.5 rounded-full border border-stone-700 text-stone-300 text-xs font-medium hover:bg-stone-800 transition-colors"
                data-testid="cookie-settings-accept-all"
              >
                Alle akzeptieren
              </button>
            </div>
            <p className="text-[10px] text-stone-500 mt-2.5 leading-snug">
              Deine Entscheidung wird mit Zeitpunkt &amp; Version protokolliert und ist jederzeit auf der{' '}
              <Link to="/cookies" className="underline hover:text-stone-300">Cookie-Seite</Link> änderbar.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
